import { useState, useCallback } from "react";
import {
  IndicatorState,
  BaseIndicatorConfig,
} from "../lib/interface/Indicator.interface";
import { useIndicatorRegistry } from "../lib/registry/IndicatorRegistry";

/**
 * Hook for managing indicator enable/disable state
 */
export function useIndicatorState(): IndicatorState {
  const registry = useIndicatorRegistry();

  // Initialize state with default values from registry
  const [enabledStates, setEnabledStates] = useState<Record<string, boolean>>(
    () => {
      const initialState: Record<string, boolean> = {};
      registry.getConfigs().forEach((config: BaseIndicatorConfig) => {
        initialState[config.id] = config.defaultEnabled;
      });
      return initialState;
    }
  );

  const toggle = useCallback((id: string, enabled?: boolean) => {
    setEnabledStates((prev) => ({
      ...prev,
      [id]: enabled !== undefined ? enabled : !prev[id],
    }));
  }, []);

  const isEnabled = useCallback(
    (id: string): boolean => {
      return enabledStates[id] ?? false;
    },
    [enabledStates]
  );

  const getEnabled = useCallback((): string[] => {
    return Object.entries(enabledStates)
      .filter(([, enabled]) => enabled)
      .map(([id]) => id);
  }, [enabledStates]);

  return {
    enabledStates,
    toggle,
    isEnabled,
    getEnabled,
  };
}
